import { getFamily } from '../api/family';
import { userMessage } from '../api/client';
import { dailyAllowance } from '../lib/group';
import { el, showToast } from '../ui/dom';

const QUICK: Record<string, string> = {
  connect:
    'On any Remindo: open Remindos → Add Remindo, then follow the on-screen steps to join this Group.',
  wifi:
    'On the Remindo: Settings → Wi-Fi setup. Scan the QR or join the Remindo hotspot, then choose your 2.4 GHz network.',
  intercom:
    'Hold the B button, speak, then release to send. Announcement needs at least two Remindos in the Group.',
  reset:
    'On the device: Settings → Reset. This clears local Wi-Fi and pairing on that Remindo. Your Group cloud data stays until you remove the device from the web.',
};

function appendMsg(log: HTMLElement, text: string, who: 'bot' | 'user'): void {
  log.append(el('div', { className: `ai-msg ${who}` }, text));
  log.scrollTop = log.scrollHeight;
}

export async function renderSupport(container: HTMLElement): Promise<void> {
  container.innerHTML = '<p class="list-empty">Loading…</p>';

  try {
    const fam = await getFamily();
    container.innerHTML = '';

    const deviceCount = Math.max(1, fam.device_count || 1);
    const perDevice = fam.fair_use.per_device ?? 50;
    const allowance = Math.max(
      fam.fair_use.daily_limit || 0,
      dailyAllowance(deviceCount, perDevice),
    );
    const fuLine =
      deviceCount === 1
        ? `With 1 Remindo, that's ${allowance} voice requests a day.`
        : `With ${deviceCount} Remindos, that's ${allowance} voice requests a day for this Group.`;

    const ask = el('div', { className: 'card ai-help' });
    ask.append(
      el('div', { className: 'card-title' }, 'Ask Remindo'),
      el('div', { className: 'card-note' }, 'Ask a question or share feedback.'),
    );
    const log = el('div', { className: 'ai-help-log' });
    log.append(el('div', { className: 'ai-msg bot' }, 'How can I help?'));
    ask.append(log);

    const quick = el('div', { className: 'ai-quick' });
    const topics: [string, string][] = [
      ['connect', 'Connect another Remindo'],
      ['wifi', 'Change Wi-Fi'],
      ['intercom', 'Announcement'],
      ['reset', 'Reset'],
    ];
    for (const [id, label] of topics) {
      const b = el('button', { type: 'button' }, label) as HTMLButtonElement;
      b.onclick = () => {
        appendMsg(log, label, 'user');
        appendMsg(log, QUICK[id] ?? 'Sorry — try again.', 'bot');
      };
      quick.append(b);
    }
    ask.append(quick);

    const compose = el('div', { className: 'ai-compose' });
    const input = el('input', {
      type: 'text',
      placeholder: 'Ask a question or share an idea',
      maxlength: '300',
    }) as HTMLInputElement;
    const send = el('button', { type: 'button' }, 'Send') as HTMLButtonElement;
    const sendAsk = () => {
      const q = input.value.trim();
      if (!q) return;
      input.value = '';
      appendMsg(log, q, 'user');
      const lower = q.toLowerCase();
      let answer =
        'Thanks — we will use this to improve Remindo. For account help, email support@remindo.app with your Group ID.';
      if (/wifi|wi-fi|network/.test(lower)) answer = QUICK.wifi;
      else if (/connect|add.*remindo|another/.test(lower)) answer = QUICK.connect;
      else if (/announce|intercom|b button/.test(lower)) answer = QUICK.intercom;
      else if (/reset|factory/.test(lower)) answer = QUICK.reset;
      else if (/feedback|suggest|hope|wish/.test(lower)) {
        answer = 'Thanks for the feedback. Only the message you submit is noted.';
        showToast('Feedback noted');
      }
      appendMsg(log, answer, 'bot');
    };
    send.onclick = sendAsk;
    input.onkeydown = (e) => {
      if (e.key === 'Enter') sendAsk();
    };
    compose.append(input, send);
    const privacy = el('div', { className: 'card-note' }, 'Only the message you submit is sent.');
    privacy.style.marginTop = '8px';
    privacy.style.marginBottom = '0';
    ask.append(compose, privacy);
    container.append(ask);

    const fair = el('div', { className: 'card' });
    const fuNote = el('div', { className: 'card-note' });
    fuNote.style.lineHeight = '1.65';
    fuNote.style.marginBottom = '0';
    /* Copy matches Remindo_WebApp_v7_topnav.html fu1 / fu2 */
    fuNote.innerHTML = `
      <p style="margin-bottom:8px;"><b>Each Remindo includes ${perDevice} voice requests per day.</b> ${fuLine} It resets overnight.</p>
      <p style="margin-bottom:0;">Scheduled reminders and Announcements don't count. Only talking to Remindo does.</p>`;
    fair.append(el('div', { className: 'card-title' }, 'Fair use'), fuNote);
    container.append(fair);

    const footer = el('footer', { className: 'site-footer span-full' });
    const nav = el('nav', { className: 'footer-links' });
    nav.setAttribute('aria-label', 'Remindo website links');
    const links: [string, string][] = [
      ['https://remindo.app', 'remindo.app'],
      ['mailto:support@remindo.app', 'support@remindo.app'],
      ['https://remindo.app/legal', 'Terms'],
      ['https://remindo.app/legal', 'Privacy'],
      ['https://remindo.app/legal#fair-use', 'Fair Use'],
    ];
    for (const [href, label] of links) {
      const a = el('a', { href }, label) as HTMLAnchorElement;
      if (href.startsWith('http')) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
      nav.append(a);
    }
    footer.append(nav, el('div', { className: 'copyright' }, '© 2026 Remindo'));
    container.append(footer);
  } catch (e) {
    container.innerHTML = '';
    container.append(el('p', { className: 'list-empty' }, userMessage(e)));
  }
}
